// The renderer, with no DOM in it.
//
// This is the arithmetic half of what used to be MandGen.js's `renderRegion`:
// bounds in, an RGBA pixel buffer out. It touches no canvas, no document and no
// window, so the *same code* runs in a browser, in Node (print-resolution
// renders, PHASE-2-PLAN D4), in a Worker (the og:image unfurl render, P1) and —
// for the first time — in a plain unit test.
//
// That last one is not a side benefit. jsdom has no 2D canvas, so while this
// code lived behind `document.createElement("canvas")` *nothing* that called it
// could be tested: the numerics underwriting the product's entire uniqueness
// guarantee had no coverage at all. See renderCore.test.js.
//
// The browser tail — canvas, putImageData, toDataURL — stays in MandGen.js and
// is now about five lines. That split is what the seam comment in MandGen.js
// promised: "Phase 2 swaps rendering locally for rendering authoritatively in
// this one function — no caller changes."

// Note the explicit .js extensions, which the rest of the app omits. Webpack and
// Jest resolve either form, but Node's ESM loader requires the extension — and
// keeping this file directly `import`-able by plain `node`, with no build step
// and no copy, is the point of the whole split. Anything this module pulls in
// has to follow the same rule.
import { calcReal, calcImaginary } from './pointer.js';
import { PALETTE_LUT, INTERIOR_RGB } from './palette.js';
import { colourMapFor, paletteIndexFor, MAP_STOPS } from './colourMap.js';
import { escapeVelocity, MAX_ITERATIONS, BAILOUT } from './escape.js';

// The escape maths moved to escape.js so colourMap.js could sample a region
// without importing this file back. Re-exported because this is still where
// callers expect to find it, and because a module boundary drawn to break a
// dependency cycle should not become an API change for everyone else.
export { escapeVelocity, MAX_ITERATIONS, BAILOUT };

// Linear supersampling factor: the escape maths + colouring run on a grid this
// many times finer in each axis, then we box-average SUPERSAMPLE^2 samples back
// down to each output pixel. This anti-aliases every edge — filigree, the set
// boundary, and the tiny embedded islands that would otherwise be hard 1px
// specks. Cost is ~SUPERSAMPLE^2× the render, paid once per zoom.
export const SUPERSAMPLE = 2;

// sRGB byte → linear-light, for gamma-correct downsampling. Averaging colours in
// raw sRGB darkens high-contrast edges (a dark island speck against bright gold
// would crush toward black); averaging in linear light blends them correctly.
const SRGB_TO_LINEAR = new Float32Array(256);
for (let i = 0; i < 256; i++) {
    const c = i / 255;
    SRGB_TO_LINEAR[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

const linearToByte = (v) => {
    const s = v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
    return Math.max(0, Math.min(255, Math.round(s * 255)));
};

// Render one region to an RGBA buffer, `width * height * 4` bytes, fully opaque.
// Row-major from the top-left, i.e. exactly the layout ImageData wants, so the
// browser tail can hand it straight to putImageData with no copy or conversion.
//
// Per output pixel, on a SUPERSAMPLE×-finer grid:
//   1. Escape maths for every subpixel → a smoothed value (NaN = interior).
//   2. Look that value up in the region's colour map (colourMap.js) to get a
//      palette entry. The map is derived from the REGION, not from this render,
//      which is what makes the colours independent of the output size.
//   3. Accumulate in linear light and box-average down to the output pixel.
//
// All three happen in one fused pass, and that is a memory result rather than a
// stylistic one. The old renderer could not fuse them: histogram equalisation
// needed every escape value in hand before it could rank any of them, so a render
// held a Float64Array of every subpixel (8 bytes each) AND a plain Array of every
// escaped subpixel's index, then sorted the latter. Those two were the whole of
// F3's measured 2.31GB peak at 300dpi. A region-derived map needs no such
// broadside: each subpixel is coloured the instant it is computed and then
// discarded, so peak memory is the OUTPUT BUFFER and nothing else.
export const renderPixels = (realMin, realMax, imaginaryMin, imaginaryMax, width, height) => {

    const hiW = width * SUPERSAMPLE;
    const hiH = height * SUPERSAMPLE;

    // Derived once for the whole region, and cached across renders of it — so a
    // gallery entry's thumbnail and its full-screen render share this table, and
    // therefore share their colours.
    const map = colourMapFor({ realMin, realMax, imaginaryMin, imaginaryMax });

    // A region where nothing in the reference lattice escaped, yet some subpixel
    // here does: a box essentially inside the set, clipped by a hairline of
    // exterior too fine for the lattice to catch. Those subpixels sit hard against
    // the set boundary, which is the top of the ramp — the right answer, and a
    // defined one, which is what matters.
    const topIndex = MAP_STOPS - 1;

    const [ir, ig, ib] = INTERIOR_RGB;
    const interiorR = SRGB_TO_LINEAR[ir];
    const interiorG = SRGB_TO_LINEAR[ig];
    const interiorB = SRGB_TO_LINEAR[ib];

    const data = new Uint8ClampedArray(width * height * 4);
    const samples = SUPERSAMPLE * SUPERSAMPLE;

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {

            let lr = 0;
            let lg = 0;
            let lb = 0;

            for (let dy = 0; dy < SUPERSAMPLE; dy++) {
                const imaginary = calcImaginary(y * SUPERSAMPLE + dy, imaginaryMin, imaginaryMax, hiH);

                for (let dx = 0; dx < SUPERSAMPLE; dx++) {
                    const real = calcReal(x * SUPERSAMPLE + dx, realMin, realMax, hiW);
                    const value = escapeVelocity(real, imaginary);

                    if (Number.isNaN(value)) {
                        lr += interiorR;
                        lg += interiorG;
                        lb += interiorB;
                        continue;
                    }

                    const j = (map === null ? topIndex : paletteIndexFor(map, value)) * 3;
                    lr += SRGB_TO_LINEAR[PALETTE_LUT[j]];
                    lg += SRGB_TO_LINEAR[PALETTE_LUT[j + 1]];
                    lb += SRGB_TO_LINEAR[PALETTE_LUT[j + 2]];
                }
            }

            const q = (y * width + x) * 4;
            data[q] = linearToByte(lr / samples);
            data[q + 1] = linearToByte(lg / samples);
            data[q + 2] = linearToByte(lb / samples);
            data[q + 3] = 255;
        }
    }

    return data;
};
